import { useState, useMemo } from 'react';
import { Solicitud } from '@/types/solicitud';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const FILTER_FIELDS = [
  { value: 'nombre', label: 'Nombre Completo' },
  { value: 'clienteEmpresa', label: 'Empresa' },
  { value: 'producto', label: 'Producto' },
  { value: 'asesorNombre', label: 'Asesor' },
  { value: 'solicitudEstatus', label: 'Estatus' },
  { value: 'solicitudNumeroControl', label: 'Número de Control' },
  { value: 'montoSolicitado', label: 'Monto Solicitado' },
  { value: 'montoAutorizado', label: 'Monto Autorizado' },
  { value: 'clienteNuevoRenovacion', label: 'Nuevo/Renovación' },
  { value: 'regionNombre', label: 'Región' },
];

export function getFieldValue(solicitud: Solicitud, field: string): string {
  if (field === 'nombre') {
    const nombre = solicitud.clienteNombre || '';
    const apellidoP = solicitud.clienteApellidoPaterno || '';
    const apellidoM = solicitud.clienteApellidoMaterno || '';
    return `${nombre} ${apellidoP} ${apellidoM}`.trim();
  }
  const value = (solicitud as any)[field];
  return value !== null && value !== undefined ? String(value) : '';
}

export function matchesFilter(solicitud: Solicitud, field: string, value: string): boolean {
  const fieldValue = getFieldValue(solicitud, field);
  if (value === '*') return fieldValue.trim() !== '';
  if (value === '""' || value === '') return fieldValue.trim() === '';
  const normalizedValue = removeAccents(value.toLowerCase().trim());
  const normalizedFieldValue = removeAccents(fieldValue.toLowerCase());
  return normalizedFieldValue.includes(normalizedValue);
}

export interface UseSolicitudFilterOptions {
  /** Filtro adicional (ej. por estatus fijo) */
  extraFilter?: (s: Solicitud) => boolean;
}

export function useSolicitudFilter(
  solicitudes: Solicitud[],
  options: UseSolicitudFilterOptions = {}
) {
  const { extraFilter } = options;
  const [filterField, setFilterField] = useState<string>('nombre');
  const [filterValue, setFilterValue] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const baseList = useMemo(() => {
    let list = solicitudes;
    if (filterValue.trim() !== '') {
      list = list.filter((s) => matchesFilter(s, filterField, filterValue));
    }
    if (extraFilter) {
      list = list.filter(extraFilter);
    }
    return list;
  }, [solicitudes, filterField, filterValue, extraFilter]);

  const filtered = useMemo(() => {
    if (searchTerm.trim() === '') return baseList;
    const term = removeAccents(searchTerm.toLowerCase());
    return baseList.filter((s) => {
      const nombreCompleto = removeAccents(
        `${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`.trim().toLowerCase()
      );
      const empresa = removeAccents((s.clienteEmpresa || '').toLowerCase());
      const control = removeAccents((s.solicitudNumeroControl || '').toLowerCase());
      const producto = removeAccents((s.producto || '').toLowerCase());
      const asesor = removeAccents((s.asesorNombre || '').toLowerCase());
      const region = removeAccents((s.regionNombre || '').toLowerCase());
      return (
        nombreCompleto.includes(term) ||
        empresa.includes(term) ||
        control.includes(term) ||
        producto.includes(term) ||
        asesor.includes(term) ||
        region.includes(term)
      );
    });
  }, [baseList, searchTerm]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterValue('');
  };

  return {
    filterField,
    setFilterField,
    filterValue,
    setFilterValue,
    searchTerm,
    setSearchTerm,
    baseList,
    filtered,
    clearFilters,
  };
}

export interface SolicitudFilterUIProps {
  filterField: string;
  setFilterField: (v: string) => void;
  filterValue: string;
  setFilterValue: (v: string) => void;
  searchTerm?: string;
  setSearchTerm?: (v: string) => void;
  filteredCount: number;
  baseCount: number;
  /** Texto opcional debajo del contador, ej. "solicitudes contactadas" */
  countLabel?: string;
}

export function SolicitudFilterUI({
  filterField,
  setFilterField,
  filterValue,
  setFilterValue,
  filteredCount,
  baseCount,
  countLabel = 'solicitudes',
}: SolicitudFilterUIProps) {
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campo</label>
          <select
            value={filterField}
            onChange={(e) => {
              setFilterField(e.target.value);
              setFilterValue('');
            }}
            className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            {FILTER_FIELDS.map((field) => (
              <option key={field.value} value={field.value}>{field.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
          <Input
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder='Use * para campo lleno, "" para vacío'
          />
        </div>
        <div>
          <Button variant="secondary" onClick={() => setFilterValue('')}>
            Limpiar Filtro
          </Button>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <div>
          Mostrando <span className="font-medium text-gray-700">{filteredCount}</span> de{' '}
          <span className="font-medium text-gray-700">{baseCount}</span> {countLabel}
        </div>
        {filterValue && (
          <div className="text-gray-400">
            Filtro: {FILTER_FIELDS.find((f) => f.value === filterField)?.label} = {filterValue}
          </div>
        )}
      </div>
    </div>
  );
}
